import React, { useState, useEffect } from "react";
import { Select, Button, InputNumber, Checkbox, Spin } from "antd";
import { MinusOutlined, PlusOutlined, DownOutlined, UpOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import api from "../../services/Api";

const { Option } = Select;

/*
 * Etusivun pikahaku (Mikan mockup 8.8.2026): kompakti kortti limittäin heron
 * alareunan päälle. Kolme suodatinsolua (Ilmoitustyyppi | Alue | Viikko)
 * vaalealla taustalla ja hienovaraisilla pystyerottimilla + Näytä kohteet.
 * "+ Lisää suodattimia" avaa lisärivin samaan korttiin (vuodepaikat, hinta,
 * omistusmuoto, ominaisuudet). Vie /kohteet-sivulle filter_*-parametreilla —
 * ei omaa hakulogiikkaa.
 */
const AuctionSearchBar = () => {
    const [types, setTypes] = useState(["sale"]);
    const [areas, setAreas] = useState([]);
    const [loadingAreas, setLoadingAreas] = useState(false);
    const [selectedAreas, setSelectedAreas] = useState([]);
    const [weeks, setWeeks] = useState([]);
    const [lisaaAuki, setLisaaAuki] = useState(false);
    const [beds, setBeds] = useState(null);
    const [priceMax, setPriceMax] = useState(null);
    const [ownership, setOwnership] = useState([]);
    const [attrs, setAttrs] = useState([]);
    const navigate = useNavigate();

    const typeOptions = [
        { label: "Myytävät", value: "sale" },
        { label: "Vuokrattavat", value: "rent" },
        { label: "Huutokaupat", value: "auction" },
    ];

    const weekOptions = ["2-7", "8-18", "19-32", "33-51", "52-1"];

    const attributesOptions = [
        { label: "Sauna", value: "hasSauna" },
        { label: "Parveke / Terassi", value: "hasBalcony" },
    ];

    const ownershipOptions = [
        { label: "Viikko", value: "week" },
        { label: "Osaomistus", value: "parttime" },
        { label: "Muu omistus", value: "other" },
    ];

    useEffect(() => {
        const fetchAreas = async () => {
            setLoadingAreas(true);

            try {
                const response = await api.getAvailableAreasAuctions();
                setAreas(response);
            } catch (error) {
                console.error("Error fetching areas:", error);
            } finally {
                setLoadingAreas(false);
            }
        };

        fetchAreas();
    }, []);

    const toggleType = (value) => {
        const uusi = types.includes(value) ? types.filter((t) => t !== value) : [...types, value];
        if (uusi.length < 1) return;
        setTypes(uusi);
    };

    const toggleWeek = (value) => {
        setWeeks((vanha) => (vanha.includes(value) ? vanha.filter((w) => w !== value) : [...vanha, value]));
    };

    const handleSearch = () => {
        const params = new URLSearchParams();
        if (types.length) params.set("filter_types", types.join(","));
        if (selectedAreas.length) params.set("filter_areas", selectedAreas.join(","));
        if (weeks.length) params.set("filter_weeks", weeks.join(","));
        if (beds && beds > 2) params.set("filter_beds", beds);
        if (priceMax) params.set("filter_price_max", priceMax);
        if (ownership.length) params.set("filter_ownership", ownership.join(","));
        if (attrs.length) params.set("filter_attrs", attrs.join(","));

        navigate(`/kohteet?${params.toString()}`);
    };

    const soluLabel = "text-[12px] font-medium text-gray-500 mb-1.5";
    const solu = { background: "#f7f9f9", borderRadius: 10 };

    const tyyppiNappi = (aktiivinen) =>
        `px-3 py-1.5 rounded-lg border text-[13px] transition-colors cursor-pointer select-none whitespace-nowrap ${
            aktiivinen
                ? "bg-primary text-white border-primary font-medium"
                : "bg-white text-gray-700 border-gray-300 hover:border-primary"
        }`;

    const viikkoNappi = (aktiivinen) =>
        `px-2.5 py-1.5 rounded-lg border text-[12.5px] transition-colors cursor-pointer select-none ${
            aktiivinen
                ? "bg-primary text-white border-primary font-medium"
                : "bg-white text-gray-600 border-gray-300 hover:border-primary"
        }`;

    return (
        <div
            className="bg-white text-left"
            style={{ borderRadius: 14, border: "1px solid #e5e7e7", boxShadow: "0 8px 24px rgba(0,0,0,.10)" }}
        >
            <div className="px-5 pt-4 pb-5">
                <h2 className="text-[17px] font-semibold text-primary mb-3">Etsi kohteita</h2>

                <div className="flex flex-col lg:flex-row lg:items-stretch gap-3 lg:gap-0">
                    {/* Solu: Ilmoitustyyppi */}
                    <div className="px-3.5 py-2.5" style={solu}>
                        <div className={soluLabel}>Ilmoitustyyppi</div>
                        <div className="flex flex-wrap gap-1.5">
                            {typeOptions.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={tyyppiNappi(types.includes(option.value))}
                                    onClick={() => toggleType(option.value)}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="hidden lg:block w-px bg-gray-200 mx-3 my-1" />

                    {/* Solu: Alue */}
                    <div className="lg:w-52 px-3.5 py-2.5" style={solu}>
                        <div className={soluLabel}>Alue</div>
                        <Select
                            mode="multiple"
                            value={selectedAreas}
                            onChange={setSelectedAreas}
                            allowClear
                            className="w-full"
                            maxTagCount={"responsive"}
                            placeholder="Valitse alue"
                        >
                            {loadingAreas && (
                                <Option value="loading" disabled>
                                    <Spin />
                                </Option>
                            )}
                            {areas?.map((area) => (
                                <Option key={area.area} value={area.area} disabled={area.auctions === 0}>
                                    {area.area}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    <div className="hidden lg:block w-px bg-gray-200 mx-3 my-1" />

                    {/* Solu: Viikko */}
                    <div className="lg:flex-1 px-3.5 py-2.5" style={solu}>
                        <div className={soluLabel}>Viikko</div>
                        <div className="flex flex-wrap gap-1.5">
                            {weekOptions.map((week) => (
                                <button
                                    key={week}
                                    type="button"
                                    className={viikkoNappi(weeks.includes(week))}
                                    onClick={() => toggleWeek(week)}
                                >
                                    {week}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Hakupainike */}
                    <div className="lg:ml-4 flex items-end lg:items-center">
                        <Button
                            type="primary"
                            onClick={handleSearch}
                            className="w-full lg:w-[150px] h-10 rounded-lg font-medium"
                        >
                            Näytä kohteet
                        </Button>
                    </div>
                </div>

                <button
                    type="button"
                    className="mt-3 text-[13px] text-gray-500 hover:text-gray-700 underline underline-offset-2"
                    onClick={() => setLisaaAuki(!lisaaAuki)}
                >
                    {lisaaAuki ? <UpOutlined className="mr-1 text-[10px]" /> : <DownOutlined className="mr-1 text-[10px]" />}
                    {lisaaAuki ? "Vähemmän suodattimia" : "Lisää suodattimia"}
                </button>

                {lisaaAuki && (
                    <div className="mt-3 pt-4 border-t border-gray-100 flex flex-col sm:flex-row sm:flex-wrap gap-x-10 gap-y-5">
                        <div>
                            <div className={soluLabel}>Vuodepaikat min</div>
                            <div className="inline-flex items-center h-9 border border-gray-300 rounded-lg overflow-hidden bg-white">
                                <button
                                    type="button"
                                    className="h-full px-3 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                                    disabled={(beds || 2) <= 2}
                                    onClick={() => setBeds(Math.max(2, (beds || 2) - 1))}
                                >
                                    <MinusOutlined />
                                </button>
                                <div className="w-9 text-center text-[14px]">{beds || "2"}</div>
                                <button
                                    type="button"
                                    className="h-full px-3 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                                    disabled={(beds || 2) >= 8}
                                    onClick={() => setBeds(Math.min(8, (beds || 2) + 1))}
                                >
                                    <PlusOutlined />
                                </button>
                            </div>
                        </div>

                        {!types.includes("auction") && (
                            <div className="w-36">
                                <div className={soluLabel}>Hinta max</div>
                                <InputNumber
                                    min={0}
                                    max={1000000}
                                    value={priceMax}
                                    onChange={setPriceMax}
                                    className="w-full"
                                    placeholder="Syötä hinta"
                                />
                            </div>
                        )}

                        <div>
                            <div className={soluLabel}>Omistusmuoto</div>
                            <Checkbox.Group value={ownership} onChange={setOwnership}>
                                <div className="flex flex-wrap gap-x-5 gap-y-2">
                                    {ownershipOptions.map((option) => (
                                        <Checkbox key={option.value} value={option.value}>
                                            <span className="text-gray-600">{option.label}</span>
                                        </Checkbox>
                                    ))}
                                </div>
                            </Checkbox.Group>
                        </div>

                        <div>
                            <div className={soluLabel}>Ominaisuudet</div>
                            <Checkbox.Group value={attrs} onChange={setAttrs}>
                                <div className="flex flex-wrap gap-x-5 gap-y-2">
                                    {attributesOptions.map((option) => (
                                        <Checkbox key={option.value} value={option.value}>
                                            <span className="text-gray-600">{option.label}</span>
                                        </Checkbox>
                                    ))}
                                </div>
                            </Checkbox.Group>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AuctionSearchBar;
